import { Children, cloneElement, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useURLQuery } from '../../Common/Hook/urlQuery';
import { useAgenciesGetQuery } from '../../Common/Queries/agenciesQueries';
import { useApplicationsGetQuery } from '../../Common/Queries/applicationsQueries';
import { useFiltersGetQuery } from '../../Common/Queries/filterQueries';
import { usePositionsGetQuery } from '../../Common/Queries/positionsQueries';

import { isLogin, isValidDate, isValidID } from '../../utils';

import { ALL_DATA_LIMIT, AuthContext, DURATION_TIME, ROLES, ROUND_RESPONSE } from '../../constants';

import { getFromLocalStorage, removeFromLocalStorage } from '../../services/localStorage';

export const validateFilterObject = (filterObject) => {
  const keyArray = [
    'positionID',
    'recruiterID',
    'agencyIDs',
    'noticePeriod',
    'roundStatus',
    'rounds',
    'streams',
    'searchValue',
    'page',
    'order',
    'orderBy',
    'createdAt',
    'updatedAt',
  ];
  return keyArray.every((key) => {
    if (
      key === keyArray[0] ||
      key === keyArray[1] ||
      key === keyArray[2] ||
      key === keyArray[3] ||
      key === keyArray[4] ||
      key === keyArray[5]
    ) {
      return filterObject[key] !== undefined && Array.isArray(filterObject[key]);
    }
    if (key === keyArray[6]) {
      return (
        typeof filterObject[key] === 'string' ||
        (filterObject[key] !== undefined &&
          typeof filterObject[key] === 'number' &&
          filterObject[key] > 0)
      );
    }
    if (key === keyArray[7]) {
      return filterObject[key] !== undefined && typeof filterObject[key] === 'string';
    }
    if (key === keyArray[8]) {
      return (
        filterObject[key] !== undefined &&
        typeof filterObject[key] === 'number' &&
        filterObject[key] >= 0
      );
    }
    if (key === keyArray[9]) {
      return (
        filterObject[key] === '' || filterObject[key] === 'asc' || filterObject[key] === 'desc'
      );
    }
    if (key === keyArray[10]) {
      return (
        filterObject[key] === '' ||
        filterObject[key] === 'name' ||
        filterObject[key] === 'positionTitle' ||
        filterObject[key] === 'updatedAt' ||
        filterObject[key] === 'createdAt'
      );
    }
    return (
      typeof filterObject[key] === 'object' &&
      Object.keys(filterObject[key]).length === 3 &&
      Object.keys(filterObject[key]).every((k) => {
        if (k === 'fromDate' || k === 'toDate')
          return filterObject[key][k] === '' || isValidDate(filterObject[key][k]);
        if (k === 'includeDate') return typeof filterObject[key][k] === 'boolean';
        return false;
      })
    );
  });
};

const initialValues = {
  streams: '',
  recruiterID: [],
  agencyIDs: [],
  noticePeriod: [],
  roundStatus: [],
  detailedAppStatus: [],
  rounds: [],
  order: '',
  orderBy: '',
  searchValue: '',
  page: 0,
  positionID: [],
  taskStatus: '',
};

export const ApplicationBetaListContainer = ({ children }) => {
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles?.[0];
  const positionList = usePositionsGetQuery(
    { limit: ALL_DATA_LIMIT },
    { enabled: isLogin(), refetchInterval: DURATION_TIME, refetchIntervalInBackground: true }
  );

  const allAgencyList = useAgenciesGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && role !== ROLES.AGENCY,
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const navigate = useNavigate();

  const urlQuery = useURLQuery();
  const positionData = positionList?.data?.data || [];
  const initialFilterValues = getFromLocalStorage(`${userInfo?.sub}-application`);

  const filterValuesObj = initialFilterValues && JSON.parse(initialFilterValues);

  useEffect(() => {
    return () => {
      removeFromLocalStorage(`${userInfo?.sub}-application`);
    };
  }, [userInfo]);

  const initialPositionID = isValidID(urlQuery.get('positionID')) && urlQuery.get('positionID');
  const initialStreamID = isValidID(urlQuery.get('streamIDs')) && urlQuery.get('streamIDs');
  const initialRecruiterID = isValidID(urlQuery.get('recruiterID')) && urlQuery.get('recruiterID');
  const initialAgencyId = isValidID(urlQuery.get('agencyIDs')) && urlQuery.get('agencyIDs');

  let selectedPositionIDs = positionData
    .filter((position) => position.stream?.id === Number(initialStreamID))
    .map((position) => position.id);

  const initialStatus = urlQuery.get('status');
  const initialRound = urlQuery.get('rounds');
  const initialRoundStatus = urlQuery.get('roundStatus');
  const initialNoticePeriod = urlQuery.get('noticePeriod');
  const initialDetailedAppStatus = urlQuery.get('detailedAppStatus');
  const initialSearch = urlQuery.get('searchParam');
  const initialPage = isValidID(urlQuery.get('page')) && urlQuery.get('page');
  const initialTaskStatus = urlQuery.get('taskStatus');

  let defaultStatus;

  if (initialStatus != null && initialStatus !== 'ALL') {
    defaultStatus = initialStatus;
  } else if (!initialSearch && filterValuesObj?.status && filterValuesObj.status !== 'ALL') {
    defaultStatus = filterValuesObj.status;
  } else {
    defaultStatus = 'ALL';
  }

  const [newApplicationStatus, setNewApplicationStatus] = useState(defaultStatus);

  if (
    !initialSearch &&
    selectedPositionIDs.length === 0 &&
    filterValuesObj &&
    validateFilterObject(filterValuesObj)
  )
    selectedPositionIDs = filterValuesObj.positionID;

  const initialFilterValuesToApply = {
    ...initialValues,
    order: 'desc',
    orderBy: 'updatedAt',
    page: initialPage ? Number(initialPage - 1) : 0,
    status: newApplicationStatus ?? initialStatus,
    searchValue: initialSearch || '',
    taskStatus: initialTaskStatus || (filterValuesObj?.taskStatus ?? ''),
    positionID: initialPositionID
      ? initialPositionID.split(',').map((item) => Number(item))
      : selectedPositionIDs,
    ...(filterValuesObj &&
    validateFilterObject(filterValuesObj) &&
    !initialPositionID &&
    !initialStreamID &&
    !initialRound &&
    !initialRoundStatus &&
    !initialRecruiterID &&
    !initialAgencyId &&
    !initialNoticePeriod &&
    !initialDetailedAppStatus &&
    !initialSearch
      ? {
          createdAt: filterValuesObj.createdAt,
          updatedAt: filterValuesObj.updatedAt,
          streams: filterValuesObj.streams,
          recruiterID: filterValuesObj.recruiterID,
          agencyIDs: filterValuesObj.agencyIDs,
          noticePeriod: filterValuesObj.noticePeriod,
          rounds: filterValuesObj.rounds,
          roundStatus: filterValuesObj.roundStatus,
          detailedAppStatus: filterValuesObj.detailedAppStatus ?? [],
        }
      : {
          createdAt: { fromDate: '', toDate: '', includeDate: true },
          updatedAt: { fromDate: '', toDate: '', includeDate: true },
          streams: initialStreamID || '',
          recruiterID: initialRecruiterID
            ? initialRecruiterID.split(',').map((item) => Number(item))
            : [],
          agencyIDs: initialAgencyId ? initialAgencyId.split(',').map((item) => Number(item)) : [],
          noticePeriod: initialNoticePeriod ? initialNoticePeriod.split(',') : [],
          rounds: initialRound ? initialRound.split(',') : [],
          roundStatus: initialRoundStatus ? initialRoundStatus.split(',') : [],
          detailedAppStatus: initialDetailedAppStatus ? initialDetailedAppStatus.split(',') : [],
        }),
  };

  const [filters, updateFilters] = useState(initialFilterValuesToApply);

  const urlLimit = isValidID(urlQuery.get('limit')) ? Number(urlQuery.get('limit')) : 20;
  const [limit, setLimit] = useState(urlLimit);

  const [allData, setAllData] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [page, setPage] = useState(1);

  const reqParams = useMemo(
    () => ({
      'createdAtRange.fromDate': filters.createdAt?.fromDate || '',
      'createdAtRange.toDate': filters.createdAt?.toDate || '',
      'createdAtRange.includeDate': filters.createdAt?.includeDate,
      'updatedAtRange.fromDate': filters.updatedAt?.fromDate || '',
      'updatedAtRange.toDate': filters.updatedAt?.toDate || '',
      'updatedAtRange.includeDate': filters.updatedAt?.includeDate,
      positionID: filters.positionID.join(','),
      streamIDs: filters.streams,
      recruiterID: filters.recruiterID.join(','),
      agencyIDs: filters.agencyIDs.join(','),
      noticePeriod: filters.noticePeriod.join(','),
      sort: filters.order.toUpperCase(),
      orderBy: filters.orderBy,
      updatedStatus: newApplicationStatus,
      roundStatus: filters.roundStatus.join(','),
      detailedAppStatus: filters.detailedAppStatus.join(','),
      interviewRound: filters.rounds.map((item) => ROUND_RESPONSE[item] || item).join(','),
      q: filters.searchValue,
      ...(filters.taskStatus ? { taskStatus: filters.taskStatus } : {}),
      limit,
      page: Number(filters.page + 1),
    }),
    [
      filters.createdAt?.fromDate,
      filters.createdAt?.toDate,
      filters.createdAt?.includeDate,
      filters.updatedAt?.fromDate,
      filters.updatedAt?.toDate,
      filters.updatedAt?.includeDate,
      filters.positionID,
      filters.streams,
      filters.recruiterID,
      filters.agencyIDs,
      filters.noticePeriod,
      filters.order,
      filters.orderBy,
      filters.page,
      newApplicationStatus,
      filters.roundStatus,
      filters.detailedAppStatus,
      filters.rounds,
      filters.taskStatus,
      filters.searchValue,
      limit,
    ]
  );

  const applications = useApplicationsGetQuery(reqParams, {
    queryKey: ['applications', reqParams],
  });

  const filterData = useFiltersGetQuery({ entity: 'application', field: 'status' });

  const applicationsAllData = useApplicationsGetQuery(
    useMemo(
      () => ({
        ...reqParams,
        limit: limit * 5,
        page,
      }),
      [reqParams, limit, page]
    ),
    {
      enabled,
      queryKey: ['applications-all', reqParams, limit * 5, page],
    }
  );

  useEffect(() => {
    if (applicationsAllData.isSuccess && enabled)
      setAllData([...allData, ...(applicationsAllData.data?.data || [])]);
    if (applicationsAllData.data?.meta?.total > page * (limit * 5)) setPage(page + 1);
  }, [applicationsAllData.isSuccess]);

  useEffect(() => {
    if (allData.length > 0) {
      setAllData([]);
      setPage(1);
    }
  }, [filters]);

  useEffect(() => {
    if (limit !== urlLimit && limit > 0) {
      urlQuery.set('limit', limit);
      navigate({ search: urlQuery.toString() }, { replace: true });
    }
  }, [limit]);

  const handleOnFilterChange = (updatedFilters) => {
    updateFilters((prev) => ({ ...prev, ...updatedFilters }));
  };

  useEffect(() => {
    handleOnFilterChange({ page: 0 });
  }, [limit]);

  const child = Children.only(children);

  return cloneElement(child, {
    applications,
    filterHandler: handleOnFilterChange,
    filters,
    enabled,
    setEnabled,
    allData,
    applicationsAllData,
    filterData,
    newApplicationStatus,
    setNewApplicationStatus,
    limit,
    setLimit,
    allAgencyList,
  });
};
